/**
 * What a paired headset is doing, as Open in Quest needs to know it (ADR-058): waiting in the lobby,
 * in a session (assigned or being prepared), or showing a preview. A preview opens only from the
 * lobby, and a lesson always wins over a preview.
 */
export type HeadsetModeState = 'lobby' | 'session' | 'preview';

export class HeadsetMode {
  #state: HeadsetModeState = 'lobby';

  get state(): HeadsetModeState {
    return this.#state;
  }

  /**
   * `preview.open`: whether it may open now -- no preview showing, and nothing running or starting
   * here (`busyHere`: a class's lesson, one practised alone, a lesson being prepared). A `session`
   * with nothing of it here is a class that ended while the headset was offline, which no
   * `session.ended` reaches; the hub sends `preview.open` only to a socket it has parked, in no
   * session, so it is believed. When it may, the headset is showing a preview from now on.
   */
  previewOffered(busyHere: boolean): boolean {
    if (this.#state === 'preview' || busyHere) return false;
    this.#state = 'preview';
    return true;
  }

  /** `session.assigned` or `session.prepare`: true when a preview was showing and must end first. */
  sessionStarted(): boolean {
    const ending = this.#state === 'preview';
    this.#state = 'session';
    return ending;
  }

  /**
   * `session.ended` or `session.released`: back in the lobby. False during a preview: no session is
   * here (one starting ends the preview first), so the message is about none of this headset's, and
   * the session flow must not act on it -- it would take the preview down and leave this mode on
   * `preview`, refusing every later preview until a reload.
   */
  sessionEnded(): boolean {
    if (this.#state === 'preview') return false;
    this.#state = 'lobby';
    return true;
  }

  /** Sluiten, the headset's own exit, the idle timer, or a preview that could not load. */
  previewClosed(): void {
    if (this.#state === 'preview') this.#state = 'lobby';
  }

  /** Unpaired: whatever it was doing is over. */
  reset(): void {
    this.#state = 'lobby';
  }
}
